import { useParams } from 'react-router-dom'
import auth from '../../api/auth/hooks'
import { getQueryData } from '../../api/helpers'
import { useEntityParams } from './EntityParamsContext'
import {
    checkAgentOffice,
    checkAgentOwnership,
    getAgentIdFromItem,
    getAgentOfficeIdFromItem,
    setAllowedAgents,
    setAllowedPermissionTabs,
    setAllowedPhotos,
    setChangeAgent,
    setEditData,
    setExportData,
    setExportSyncWidget,
    setGoogleMaps,
    setModulePermissions,
    setNavigation,
    setPropertyStatus,
    setSubMenu,
    setTableCellData,
    setViewData,
} from './utils/utils'

// use this component to turn on/off application permissions
const AppPermissionsSwitcher = ({ children, ...props }) => {
    const { id } = useParams()

    /**
     * @param {string} permission - permission type to check
     * @param {string} module - module name (e.g., projects, properties, contacts, companies, activities, documents)
     * @param {number} itemAgentId - id of agent that created item in list (projects, properties, contacts, companies)
     * @param {number} itemAgentOfficeId - agency office id of agent that created item in list
     * @param {number} activityAgentId - id of agent that created activity
     * @param {number} documentAgentId - id of agent that created document
     *
     * entityParams - params (criteria/order) the view's GetEntity query was called with, needed to
     * match its cache key - read from the nearest EntityParamsProvider, not a prop.
     */
    let { permission, module, itemAgentId, itemAgentOfficeId, activityAgentId, documentAgentId } = props
    const entityParams = useEntityParams()

    module = module?.pathname?.includes('/') ? module.pathname.split('/')[1] || module : module

    const { data: currentAgent } = auth.CurrentAgent.useQuery({
        select: (data) => ({
            id: data.id,
            agencyOffice: data.agencyOffice,
            agentPermissions: data.agentPermissions,
        }),
    })

    const data = getQueryData([module, id], entityParams)

    /**
     * @param {number} viewAgentId - id of agent that created item in view (project, property, contact, company)
     * @param {number} viewAgentOfficeId - agency office id of agent that created item
     */
    const viewAgentId = getAgentIdFromItem(data?.item, module, id)
    const viewAgentOfficeId = getAgentOfficeIdFromItem(data?.item, module, id)

    /**
     * @param {number} currentAgentId - id of currently logged in agent
     * @param {number} currentOffice - agency office id of currently logged in agent
     */
    const { agentPermissions, id: currentAgentId, agencyOffice: currentOffice } = currentAgent ?? []

    const isCreateMode = !id && !itemAgentId && !activityAgentId && !documentAgentId
    const isAgentOwnership = isCreateMode || checkAgentOwnership(viewAgentId, currentAgentId, itemAgentId, activityAgentId, documentAgentId)
    const isAgentOffice = isCreateMode || checkAgentOffice(viewAgentOfficeId, currentOffice, itemAgentOfficeId)

    const propertyStatus = data?.item?.propertyStatus

    const permissionProps = {
        ...props,
        agentPermissions,
        isAgentOwnership,
        isAgentOffice,
        currentAgentId,
        currentOffice,
        module,
        propertyStatus,
    }
    let newProps = {}

    switch (permission) {
        case 'ModulePermissions':
            newProps = setModulePermissions(permissionProps)
            break
        case 'Navigation':
            newProps = setNavigation(permissionProps)
            break
        case 'SubMenu':
            newProps = setSubMenu(permissionProps)
            break
        case 'ViewData':
            newProps = setViewData(permissionProps)
            break
        case 'EditData':
            newProps = setEditData(permissionProps)
            break
        case 'AllowedTabs':
            newProps = setAllowedPermissionTabs(permissionProps)
            break
        case 'AllowedPhotos':
            newProps = setAllowedPhotos(permissionProps)
            break
        case 'AllowedAgents':
            newProps = setAllowedAgents(permissionProps)
            break
        case 'GoogleMaps':
            newProps = setGoogleMaps(permissionProps)
            break
        case 'PropertyStatus':
            newProps = setPropertyStatus(permissionProps)
            break
        case 'ChangeAgent':
            newProps = setChangeAgent(permissionProps)
            break
        case 'ExportSyncWidget':
            newProps = setExportSyncWidget(permissionProps)
            break
        case 'ExportData':
            newProps = setExportData(permissionProps)
            break
        case 'TableCell':
            newProps = setTableCellData(permissionProps)
            break
        default:
            console.error(`No application permission defined for: ${permission}`)
    }

    return children(newProps)
}

export default AppPermissionsSwitcher
